<template>
	<div class="map-tooltip-style legend-position" aria-label="map legend">
		<LabeledSwitch
			v-if="baseStationsStore.mode === MAP_MODE.main"
			v-model="measureEnabledRef"
			:rightLabel="$t('home-map.tools.measure')"
			class="pd-vertical"
		/>
		<LabeledSwitch
			v-if="showFillCoverageAreaSwitch"
			v-model="fillCoverageAreaRef"
			:rightLabel="$t('home-map.tools.fillCoverageArea')"
			class="pd-vertical"
		/>
		<LabeledSwitch
			v-if="baseStationsStore.mode === MAP_MODE.gnssPerGridpoint && userStore.allowToChangeMapMode && licenseStore.allowPppGp"
			v-model="showGridpointsOnlyRef"
			:aria-label="$t('home-map.tools.gridpoints')"
			:rightLabel="$t('home-map.tools.gridpoints')"
			class="pd-vertical"
		/>
		<div v-if="baseStationsStore.mode === MAP_MODE.main">
			<div v-if="userStore.showSubNetworkFilters" class="pd-vertical">
				<TypographyTitle :level="5">{{ $t('home-map.bsServiceFilter.label') }}</TypographyTitle>
				<RadioGroup
					v-model:value="bsFilterSelectedRef"
					:options="bsFilterOptions"
					option-type="button"
					size="small"
				/>
			</div>
			<div v-if="userStore.showSubNetworkFilters" class="pd-vertical">
				<TypographyTitle :level="5">{{ $t('home-map.subNetworkFilter.label') }}</TypographyTitle>
				<Select
					class="width-100"
					v-model:value="bsSnFilterSelectedRef"
					:options="bsSnFilterOptions"
					@change="onMainSubNetworkChange"
				/>
			</div>
			<TypographyTitle :level="5">{{ $t('home-map.legendStatusColors') }}</TypographyTitle>
			<div v-for="legendItem in bsStatusLegendItems" :key="legendItem.key">
				<SimpleBadge :status="legendItem.badge" :text="$t('baseStationStatus.' + legendItem.key)" />
			</div>
			<div v-if="baseStationsStore.geoFencing != null">
				<svg class="geo-fencing-arc-0" width="50" height="20" viewBox="130 180 195 170">
					<path stroke="#333333" fill="none" d="M 465 180 A 233 84 360 1 1 -2 179" stroke-width="35"></path>
					<path stroke="white" fill="none" d="M 465 180 A 233 84 360 1 1 -2 179" stroke-width="15"></path>
				</svg>
				<div class="geo-fencing-legend-0">{{ $t('home-map.legendGeoFencingLabel') }}</div>
			</div>
			<div v-if="baseStationsStore.profiles.length > 0">
				<svg class="geo-fencing-arc-1" width="50" height="20" viewBox="130 180 195 170">
					<path stroke="white" fill="none" d="M 465 180 A 233 84 360 1 1 -2 179" stroke-width="35"></path>
					<path stroke="#333333" fill="none" d="M 465 180 A 233 84 360 1 1 -2 179" stroke-width="10"></path>
				</svg>
				<div class="geo-fencing-legend-1">{{ $t('home-map.legendProfilesGeoFencingLabel') }}</div>
			</div>
			<div v-if="userStore.allowToSeeModuleStatuses && licenseStore.featureFlagJamming" class="pd-vertical">
				<svg
					class="legend-base-station-spoofing-overlay-swatch"
					width="21"
					height="21"
					viewBox="0 0 21 21"
					aria-hidden="true"
				>
					<defs>
						<pattern
							id="legendBaseStationSpoofingOverlayPattern"
							width="8"
							height="8"
							patternUnits="userSpaceOnUse"
						>
							<rect width="8" height="8" fill="rgba(80, 80, 80, 0.22)" />
							<path
								d="M -2 6 L 6 -2 M 2 10 L 10 2"
								stroke="rgba(80, 80, 80, 0.55)"
								stroke-width="1.5"
								fill="none"
							/>
						</pattern>
						<clipPath id="legendBaseStationSpoofingOverlayClip">
							<circle cx="10.5" cy="10.5" r="10.5" />
						</clipPath>
					</defs>
					<rect
						width="21"
						height="21"
						fill="url(#legendBaseStationSpoofingOverlayPattern)"
						clip-path="url(#legendBaseStationSpoofingOverlayClip)"
					/>
				</svg>
				{{ $t('home-map.legendGssOverlay') }}
			</div>
		</div>
		<div v-if="baseStationsStore.mode === MAP_MODE.coverage" class="pd-vertical">
			<TypographyTitle :level="5">{{ $t('home-map.vrsAccuracyLegend.colorBy') }}</TypographyTitle>
			<Select
				class="width-100"
				v-model:value="qualityColorModeRef"
				:options="qualityColorModeOptions"
			/>
		</div>
		<div v-show="showOptionalSubNetworkFilter && baseStationsStore.mode !== MAP_MODE.main" class="pd-vertical">
			<TypographyTitle :level="5">{{ $t('home-map.subNetworkFilter.label') }}</TypographyTitle>
			<Select
				class="width-100"
				v-model:value="bsSnFilterSelectedRef"
				:options="bsSnFilterOptions"
				@change="onMainSubNetworkChange"
			/>
		</div>
		<div v-if="baseStationsStore.mode === MAP_MODE.subNetworks" class="pd-vertical">
			<TypographyTitle :level="5">{{ $t('home-map.legendSubNetworkColors') }}</TypographyTitle>
			<div v-for="subNetwork in activeSubNetworks" :key="subNetwork.id">
				<div class="legend-gnss-bar" :style="{ backgroundColor: subNetwork.color }"></div> {{ subNetwork.title }}
			</div>
		</div>
		<div v-if="baseStationsStore.mode === MAP_MODE.coverage" class="pd-vertical">
			<TypographyTitle :level="5">{{ $t('home-map.vrsAccuracyLegend.title') }}</TypographyTitle>
			<div v-for="legendItem in vrsAccuracyLegendItems" :key="legendItem.key">
				<SimpleBadge
					:status="legendItem.color != null ? legendItem.color : legendItem.badge"
					:text="$t('home-map.vrsAccuracyLegend.' + legendItem.key)"
				/>
			</div>
		</div>
		<div v-if="baseStationsStore.mode === MAP_MODE.baselines">
			<TypographyTitle :level="5">{{ $t('home-map.legendStatusColors') }}</TypographyTitle>
			<div v-for="legendItem in baselineStatusLegendItems" :key="legendItem.key">
				<SimpleBadge :status="legendItem.badge" :text="$t('baselines.mapLegendStatuses.' + legendItem.key)" />
			</div>
			<RedoOutlined :style="{ fontSize: '20px' }" /><div class="self-baseline-legend">{{ $t('home-map.selfBaselineLegend') }}</div>
		</div>
		<div v-if="baseStationsStore.mode === MAP_MODE.gnss">
			<TypographyParagraph class="legend-gnss-text">{{ $t('home-map.legendGnssText') }}</TypographyParagraph>
		</div>
		<!-- v-show is required in order to initialize SubNetworkSelectStoreBound component -->
		<div class="pd-vertical" v-show="showStrictSubNetworkFilter">
			<div>
				<TypographyTitle :level="5">{{ $t('home-map.subNetworkFilter.label') }}</TypographyTitle>
				<SubNetworkSelectStoreBound ref="subNetworkSelect" :showLabel="false" :onChange="onSubNetworkChange"/>
			</div>
			<div class="pd-vertical">
				<CNetSelectStoreBound
					v-if="baseStationsStore.mode === MAP_MODE.gnssPerGridpoint"
					labelKey="home-map.cnetFilter.label"
				/>
			</div>
		</div>
		<div v-if="showGnssLegend">
			<TypographyTitle :level="5">{{ $t('home-map.legendGnssColors') }}</TypographyTitle>
			<div v-for="gnssColor in gnssColorItems" :key="gnssColor.code">
				<div class="legend-gnss-bar" :style="{ backgroundColor: '#' + gnssColor.color }"></div> {{ gnssColor.name }}
			</div>
		</div>
		<div class="pd-vertical" v-if="baseStationsStore.mode === MAP_MODE.gnss">
			<TypographyTitle :level="5">{{ $t('home-map.legendGnssBSTooltipItems.header') }}</TypographyTitle>
			<CheckboxesComponent v-model:value="selectedGnssTooltipOptions">
				<Checkbox v-for="tooltipOption in gnssTooltipOptions" :key="tooltipOption" :value="tooltipOption">
					{{ $t('home-map.legendGnssBSTooltipItems.options.' + tooltipOption) }}
				</Checkbox>
			</CheckboxesComponent>
		</div>
		<MapLegendGradient
			v-else-if="baseStationsStore.mode === MAP_MODE.paasStddev"
			title="home-map.legendPaasStddevColors"
			:minValue="paasStddevMin"
			:maxValue="paasStddevMax"
			:showSelect="false"
			units="home-map.legendPaasStddevUnits"
		/>
		<LabeledSwitch
			v-if="baseStationsStore.mode === MAP_MODE.coverage"
			v-model="showBaseStationTooltipsRef"
			:rightLabel="$t('home-map.tools.showBaseStationTooltips')"
			class="pd-vertical"
		/>
	</div>
</template>

<script lang="ts">
import { ref, computed, toRef } from 'vue';
import type { PropType } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { TypographyTitle, TypographyParagraph, RadioGroup, Select, Checkbox } from 'ant-design-vue';
import type { SelectValue } from 'ant-design-vue/es/select';
import { RedoOutlined } from '@ant-design/icons-vue';

import { BASE_STATION_LEGEND_ITEMS, BASELINE_LEGEND_ITEMS, VRS_ACCURACY_LEGEND_ITEMS } from '@/utils/baseStationsUtils';
import Consts from '@/api/Consts';
import MapConsts from '@/components/map/consts/MapConsts';
import type { QualityColorMode } from '@/components/map/consts/MapConsts';

import {
	useBaseStationsStore,
	SUB_NETWORK_STRICT_MODES,
	SUB_NETWORK_OPTIONAL_MODES,
	GNSS_MODES,
	FILL_COVERAGE_AREA_MODES,
} from '@/stores/baseStations';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { BsFilterValue } from '@/components/map/utils/useMapBsFilter';
import type { ActiveSubNetworkWithColor } from '@/components/map/types/mapFeatureTypes';
import { useUserStore } from '@/stores/user';
import { useLicenseStore } from '@/stores/licenseStore';
import { useSessionStore } from '@/stores/session';

import type { SelectOption } from '@/components/common/types/selectOptionTypes';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import SimpleBadge from '@/components/common/SimpleBadge.vue';
import SubNetworkSelectStoreBound from '@/components/common/controls/SubNetworkSelectStoreBound.vue';
import CNetSelectStoreBound from '@/components/common/controls/CNetSelectStoreBound.vue';
import CheckboxesComponent from '@/components/common/CheckboxesComponent.vue';

import MapLegendGradient from '@/components/map/MapLegendGradient.vue';


type SubNetworkSelectExposed = {
	selectedId: number;
	options: SelectOption[];
};

const GNSS_TOOLTIP_OPTIONS = ['satellites', 'signals', 'rtcm-messages'] as const;

type GnssTooltipOption = typeof GNSS_TOOLTIP_OPTIONS[number];


const toSelectNumber = (value: SelectValue): number | null => {
	if (typeof value === 'number') {
		return value;
	}
	if (typeof value === 'string' && value !== '') {
		const parsed = Number(value);
		return Number.isNaN(parsed) ? null : parsed;
	}
	return null;
};


export default {
	components: {
		TypographyTitle, TypographyParagraph, RadioGroup, Select, Checkbox,
		RedoOutlined,
		LabeledSwitch, MapLegendGradient,
		SimpleBadge,
		SubNetworkSelectStoreBound,
		CNetSelectStoreBound,
		CheckboxesComponent,
	},
	props: {
		measureEnabled: {
			type: Boolean,
			required: true,
		},
		fillCoverageArea: {
			type: Boolean,
			required: true,
		},
		showBaseStationTooltips: {
			type: Boolean,
			required: true,
		},
		showGridpointsOnly: {
			type: Boolean,
			required: true,
		},
		gnssSelected: {
			type: String,
			required: true,
		},
		gnssTooltipsNonNullGnss: {
			type: Array as PropType<string[]>,
			required: true,
		},
		bsFilterOptions: {
			type: Array as PropType<SelectOption<BsFilterValue>[]>,
			required: true,
		},
		bsFilterSelected: {
			type: String as PropType<BsFilterValue>,
			required: true,
		},
		bsSnFilterOptions: {
			type: Array as PropType<SelectOption[]>,
			required: true,
		},
		bsSnFilterSelected: {
			type: Number,
			required: true,
		},
		activeSubNetworks: {
			type: Array as PropType<ActiveSubNetworkWithColor[]>,
			required: true,
		},
		qualityColorMode: {
			type: String as PropType<QualityColorMode>,
			required: true,
		},
	},
	emits: {
		'update:measureEnabled': (_value: boolean) => true,
		'update:fillCoverageArea': (_value: boolean) => true,
		'update:showBaseStationTooltips': (_value: boolean) => true,
		'update:showGridpointsOnly': (_value: boolean) => true,
		'update:gnssSelected': (_value: string) => true,
		'update:bsFilterSelected': (_value: BsFilterValue) => true,
		'update:bsSnFilterSelected': (_value: number) => true,
		'update:qualityColorMode': (_value: QualityColorMode) => true,
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const measureEnabledRef = useVModel(props, 'measureEnabled', emit);
		const showGridpointsOnlyRef = useVModel(props, 'showGridpointsOnly', emit);
		const gnssSelectedRef = useVModel(props, 'gnssSelected', emit);
		const bsFilterSelectedRef = useVModel(props, 'bsFilterSelected', emit);
		// subNetworkFilterSelectedRef
		const bsSnFilterSelectedRef = useVModel(props, 'bsSnFilterSelected', emit); // 1, 2, includes value == 0
		const fillCoverageAreaRef = useVModel(props, 'fillCoverageArea', emit);
		const showBaseStationTooltipsRef = useVModel(props, 'showBaseStationTooltips', emit);
		const qualityColorModeRef = useVModel(props, 'qualityColorMode', emit);

		const qualityColorModeOptions = computed((): SelectOption<QualityColorMode>[] => [
			{
				label: t('home-map.tooltip.vrsPst'),
				value: MapConsts.QUALITY_COLOR_MODE.vrsPst,
			},
			{
				label: t('home-map.tooltip.qEff'),
				value: MapConsts.QUALITY_COLOR_MODE.qEff,
			},
		]);

		const gnssTooltipsNonNullGnssRef = toRef(props, 'gnssTooltipsNonNullGnss');
		const gnssColorItems = computed(() => {
			if (gnssTooltipsNonNullGnssRef.value.length === 0) {
				return Consts.GNSS_DATA.filter(item => item.showInLegend);
			}
			return Consts.GNSS_DATA.filter(item => gnssTooltipsNonNullGnssRef.value.includes(item.code));
		});
		const subNetworkSelect = ref<SubNetworkSelectExposed | null>(null);
		const onSubNetworkChange = (value: number) => {
			bsSnFilterSelectedRef.value = value;
		};
		const onMainSubNetworkChange = (value: SelectValue) => {
			const numericValue = toSelectNumber(value);
			if (numericValue != null && numericValue !== 0 && subNetworkSelect.value != null) {
				subNetworkSelect.value.selectedId = numericValue;
			}
		};

		const baseStationsStore = useBaseStationsStore();
		const showStrictSubNetworkFilter = computed(() => SUB_NETWORK_STRICT_MODES.includes(baseStationsStore.mode));
		const showOptionalSubNetworkFilter = computed(() => SUB_NETWORK_OPTIONAL_MODES.includes(baseStationsStore.mode));
		const showGnssLegend = computed(() => GNSS_MODES.includes(baseStationsStore.mode));
		const showFillCoverageAreaSwitch = computed(() => FILL_COVERAGE_AREA_MODES.includes(baseStationsStore.mode));

		const gnssTooltipOptions = ref<readonly GnssTooltipOption[]>(GNSS_TOOLTIP_OPTIONS);
		const sessionStore = useSessionStore();
		const selectedGnssTooltipOptions = computed({
			get: (): string[] => sessionStore.mapModeGnssBSTooltipItems,
			set: (value: string[]) => sessionStore.updateMapModeGnssBSTooltipItems(value),
		});

		return {
			baseStationsStore,
			userStore: useUserStore(),
			licenseStore: useLicenseStore(),
			measureEnabledRef,
			showGridpointsOnlyRef,
			fillCoverageAreaRef,
			showBaseStationTooltipsRef,
			qualityColorModeRef,
			qualityColorModeOptions,
			gnssColorItems,
			bsStatusLegendItems: BASE_STATION_LEGEND_ITEMS,
			baselineStatusLegendItems: BASELINE_LEGEND_ITEMS,
			vrsAccuracyLegendItems: VRS_ACCURACY_LEGEND_ITEMS,
			gnssTooltipOptions,
			selectedGnssTooltipOptions,

			gnssSelectedRef,
			bsFilterSelectedRef,

			subNetworkSelect,
			onSubNetworkChange,

			bsSnFilterSelectedRef,
			onMainSubNetworkChange,

			paasStddevMin: MapConsts.PAAS_STDDEV_MIN,
			paasStddevMax: MapConsts.PAAS_STDDEV_MAX,

			showStrictSubNetworkFilter,
			showOptionalSubNetworkFilter,
			showGnssLegend,
			showFillCoverageAreaSwitch,
			MAP_MODE,
		};
	},
}
</script>

<style scoped>
@import '@/assets/map-legend.css';
</style>

<style scoped>
.legend-gnss-text {
	max-width: 250px;
}

.legend-base-station-spoofing-overlay-swatch {
	display: inline-block;
	vertical-align: middle;
	margin-right: 6px;
}

.legend-gnss-bar {
	display: inline-block;
	vertical-align: bottom;
	width: 10px;
	height: 14px;
	margin-inline-end: 4px;
	margin-block-end: 4px;
}

.geo-fencing-arc-0 {
	margin-top: 11px;
}

.geo-fencing-legend-0 {
	display: inline-block;
	margin-inline-start: 10px;
	margin-block-start: 4px;
	vertical-align: top;
}

.geo-fencing-arc-1 {
	margin-top: 7px;
}

.geo-fencing-legend-1 {
	display: inline-block;
	margin-inline-start: 10px;
	margin-block-start: 0px;
	vertical-align: top;
}

.self-baseline-legend {
	display: inline-block;
	margin-inline-start: 10px;
	margin-block-start: 4px;
	vertical-align: baseline;
}
</style>
